import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Star, ChevronLeft, Clock, Sparkles, Loader2, MapPin, ArrowRight, Sun, Moon, CloudSun, Zap, Heart, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLIMATE_ICONS = {
  expansion: TrendingUp,
  restructuration: TrendingDown,
  transition: RefreshCw
};

const CLIMATE_COLORS = {
  expansion: { bg: 'bg-green-500/20', border: 'border-green-500/40', text: 'text-green-400' },
  restructuration: { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-400' },
  transition: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-400' }
};

const TransitAspectCard = ({ aspect, index }) => {
  const isPositive = aspect.aspect?.nature === 'positif';
  const intensityColors = {
    fort: 'border-l-4 border-l-yellow-500',
    modéré: 'border-l-4 border-l-blue-500',
    faible: 'border-l-4 border-l-gray-400'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`p-3 bg-white rounded-lg ${intensityColors[aspect.intensity] || ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isPositive ? (
            <Heart className="h-4 w-4 text-green-400" />
          ) : (
            <Zap className="h-4 w-4 text-orange-400" />
          )}
          <span className="text-sm text-gray-900">
            <span className="text-sky-600">{aspect.transit_planet?.name}</span>
            {' '}{aspect.aspect?.symbol}{' '}
            <span className="text-purple-400">{aspect.natal_planet?.name}</span>
          </span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${isPositive ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
          {aspect.intensity}
        </span>
      </div>
      <p className="text-xs text-gray-600 mt-1">{aspect.aspect?.name}</p>
    </motion.div>
  );
};

const ClimateGauge = ({ fluidity, friction }) => (
  <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${fluidity}%` }}
      transition={{ duration: 1 }}
      className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-500 to-emerald-500"
    />
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${friction}%` }}
      transition={{ duration: 1, delay: 0.2 }}
      className="absolute right-0 top-0 h-full bg-gradient-to-l from-orange-500 to-red-500"
    />
    <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-medium text-gray-900">
      <span>Fluidité {fluidity}%</span>
      <span>Friction {friction}%</span>
    </div>
  </div>
);

const AstrozodiacTransitsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    birthDate: '',
    birthTime: '',
    birthCity: 'Paris',
    birthCountry: 'FR'
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [profilePreFilled, setProfilePreFilled] = useState(false);

  // Auto-fill from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled) {
      const newFormData = { ...formData };
      let hasPreFilled = false;
      
      if (user.first_name || user.name) {
        newFormData.firstName = user.first_name || user.name;
        hasPreFilled = true;
      }
      if (user.birth_date) {
        newFormData.birthDate = user.birth_date;
        hasPreFilled = true;
      }
      if (user.birth_time) {
        newFormData.birthTime = user.birth_time;
        hasPreFilled = true;
      }
      if (user.birth_city) {
        newFormData.birthCity = user.birth_city;
        hasPreFilled = true;
      }
      
      if (hasPreFilled) {
        setFormData(newFormData);
        setProfilePreFilled(true);
        toast.success('Données pré-remplies depuis votre profil !', {
          description: 'Modifiez si nécessaire'
        });
      }
    }
  }, [isAuthenticated, user, profilePreFilled]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.birthDate || !formData.birthCity) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setResult(null);
    setInterpretation(null);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/transits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birth_date: formData.birthDate,
          birth_time: formData.birthTime || null,
          birth_city: formData.birthCity,
          birth_country: formData.birthCountry,
          first_name: formData.firstName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data);
        toast.success('Transits analysés !');
      } else {
        toast.error(data.detail || 'Erreur de calcul');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  const getInterpretation = async () => {
    if (!result) return;
    
    setLoadingInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/interpret/transits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transits_data: result,
          first_name: formData.firstName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setInterpretation(data.interpretation);
        toast.success('Interprétation générée !');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingInterpretation(false);
    }
  };
  
  const ClimateIcon = result?.climate ? CLIMATE_ICONS[result.climate.climate_type] || CloudSun : CloudSun;
  const climateColors = result?.climate ? CLIMATE_COLORS[result.climate.climate_type] || CLIMATE_COLORS.transition : CLIMATE_COLORS.transition;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50">
      {/* Starfield */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(40)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.4 + 0.1, animationDelay: `${Math.random() * 3}s` }} />
        ))}
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-sky-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/astrozodiac" className="flex items-center gap-2 text-sky-600 hover:text-sky-500" data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <CloudSun className="h-5 w-5 text-amber-400" />
            <span className="font-bold text-gray-900">Transits & Timing</span>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Form */}
        {!result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border border-amber-200 mb-4">
                <CloudSun className="h-10 w-10 text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Transits & Timing</h1>
              <p className="text-gray-600">Découvre ton climat céleste actuel</p>
              <p className="text-sky-600 text-sm mt-2 italic">"Ce ne sont pas des prédictions, ce sont des climats"</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5 bg-white p-6 rounded-2xl border border-sky-200">
              <div>
                <Label className="text-gray-700">Prénom *</Label>
                <Input type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-firstname" />
              </div>
              
              <div>
                <Label className="text-gray-700">Date de naissance *</Label>
                <Input type="date" value={formData.birthDate} onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthdate" />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><Clock className="h-4 w-4" /> Heure (optionnel)</Label>
                <Input type="time" value={formData.birthTime} onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthtime" />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><MapPin className="h-4 w-4" /> Ville *</Label>
                <Input type="text" value={formData.birthCity} onChange={(e) => setFormData({ ...formData, birthCity: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-city" />
              </div>
              
              <Button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-semibold rounded-xl py-6"
                data-testid="calculate-btn">
                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Analyse...</> : <><Sparkles className="mr-2 h-5 w-5" />Analyser mon climat actuel</>}
              </Button>
            </form>
          </motion.div>
        )}
        
        {/* Results */}
        {result && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Climate Header */}
            <div className={`p-6 rounded-2xl ${climateColors.bg} border ${climateColors.border}`}>
              <div className="flex items-center gap-4">
                <div className={`p-4 rounded-xl bg-gray-50`}>
                  <ClimateIcon className={`h-10 w-10 ${climateColors.text}`} />
                </div>
                <div>
                  <h2 className={`text-2xl font-bold ${climateColors.text}`}>
                    {result.climate?.climate_name}
                  </h2>
                  <p className="text-gray-700">{result.climate?.climate_description}</p>
                </div>
              </div>
              
              {/* Climate Gauge */}
              <div className="mt-6">
                <ClimateGauge 
                  fluidity={result.climate?.fluidity_score || 50} 
                  friction={result.climate?.friction_score || 50} 
                />
              </div>
              
              {/* Natal Summary */}
              <div className="mt-4 flex gap-4 text-sm">
                <span className="text-gray-600">
                  <Sun className="h-4 w-4 inline mr-1 text-amber-400" />
                  {result.natal_summary?.sun}
                </span>
                <span className="text-gray-600">
                  <Moon className="h-4 w-4 inline mr-1 text-gray-700" />
                  {result.natal_summary?.moon}
                </span>
              </div>
            </div>
            
            {/* Transit Aspects */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-sky-600" />
                Transits Actifs ({result.transit_aspects?.length || 0})
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                {result.transit_aspects?.slice(0, 12).map((aspect, index) => (
                  <TransitAspectCard key={index} aspect={aspect} index={index} />
                ))}
              </div>
              
              {result.transit_aspects?.length === 0 && (
                <p className="text-gray-600 text-center py-4">Pas de transits majeurs actuellement</p>
              )}
            </div>
            
            {/* AI Interpretation */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-sky-600" />
                Interprétation IA
              </h3>
              
              {interpretation ? (
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown>{interpretation}</ReactMarkdown>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Button onClick={getInterpretation} disabled={loadingInterpretation}
                    className="bg-gradient-to-r from-sky-500 to-cyan-500 text-gray-900 rounded-xl"
                    data-testid="get-interpretation-btn">
                    {loadingInterpretation ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Analyse...</> : <><Sparkles className="mr-2 h-5 w-5" />Obtenir l'interprétation IA</>}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" onClick={() => { setResult(null); setInterpretation(null); }}
                className="border-gray-200 text-gray-700 hover:bg-white rounded-xl" data-testid="new-calc-btn">
                Actualiser
              </Button>
              <Button onClick={() => navigate('/astrozodiac/decision')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-gray-900 rounded-xl" data-testid="go-decision-btn">
                Mode Décision <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AstrozodiacTransitsPage;
